import React from 'react';
import {LEAGUES} from '../../../constants/leagues';
import {UNIT_SIZE_FOR_SUPPLY_ON_BARRACKS, UNIT_SIZE_FOR_SUPPLY_ON_OTHER} from '../../../constants/units';
import {MINUS_SIGN} from '../../../constants/text';
import {DAMAGE_DICE_MAX_VALUE, DAMAGE_DICE_MIN_VALUE} from '../../../configs/damage';
import {getPlayerBonusConfig, PLAYER_BONUS_LABELS, PLAYER_BONUS_ORDER} from '../../../configs/playerBonus';
import {getLocalBotLeagueId} from '../../../configs/localBotLeagues';
import {getVisibleBotIconPath} from '../../../utils/bots/getVisibleBotIconPath';
import {getBotDiceBonusLines} from '../../../utils/bots/getBotDiceBonusLines';
import {LOCAL_BONUS_WIN_POINTS_TYPES} from '../../../utils/bots/getLocalBotGrowthBonusNote';
import BotLeagueBonuses, {
  formatWinPoints,
  getLeagueBonus,
  getLeagueGrowthLines,
  NO_GROWTH_BONUS_TEXT,
} from './BotLeagueBonuses';

// Название лиги в родительном падеже — «значок золотого бота»
const LEAGUE_ADJECTIVES_GENITIVE = {
  [LEAGUES.BRONZE]: 'бронзового',
  [LEAGUES.SILVER]: 'серебряного',
  [LEAGUES.GOLD]: 'золотого',
  [LEAGUES.DIAMOND]: 'алмазного',
  [LEAGUES.RUBY]: 'рубинового',
  [LEAGUES.GOD]: 'божественного',
};

const formatDiceFace = (face) => (face < 0 ? `${MINUS_SIGN}${Math.abs(face)}` : `${face}`);

/**
 * Уровни бонуса бота локальной игры, которым соответствует лига: по ней бот
 * получает и значок, и прибавку к росту отрядов (см.
 * configs/localBotLeagues).
 */
const getLocalBotBonusTypes = (leagueId) => PLAYER_BONUS_ORDER
  .filter((bonusType) => getLocalBotLeagueId(bonusType) === leagueId);

// Названия уровней — те же, что на кнопке выбора бонуса
const getBonusLabel = (bonusType) => `«${PLAYER_BONUS_LABELS[bonusType]}»`;

/**
 * Что бот локальной игры получает сверх самого уровня бонуса, одной фразой:
 * прибавку к росту — по лиге-эквиваленту, кости — по уровням этой лиги (пол
 * d6 бота локальной партии задает его уровень, см. getBotD6MinRoll; если у
 * уровней одного значка он разный — берется худший).
 */
const getLocalBotExtrasText = (leagueId) => {
  const minRolls = getLocalBotBonusTypes(leagueId).map((bonusType) => getPlayerBonusConfig(bonusType).d6MinRoll || 1);
  const lines = [
    ...getLeagueGrowthLines(leagueId),
    ...getBotDiceBonusLines(Math.min(...minRolls)).map((line) => line.charAt(0).toLowerCase() + line.slice(1)),
  ];
  return lines.length > 0 ? `${lines.join('; ')}.` : NO_GROWTH_BONUS_TEXT;
};

// Уровни бонуса с очками победы к доходу — сколько каждый дает любому игроку
const WIN_POINTS_LEVELS_TEXT = LOCAL_BONUS_WIN_POINTS_TYPES
  .map((bonusType) => (
    `${getBonusLabel(bonusType)} — еще и ${
      formatWinPoints(getPlayerBonusConfig(bonusType).winPointsIncomePerCityBonus)
    } к доходу за каждый город`
  ))
  .join('; ');

const BotsRules = () => {
  const coins = <img
    className={'policy-description-icon policy-description-icon-margin-left'}
    src={'/img/icons/coins.png'}
    alt={'Монеты'}
    title={'Монеты'}
  />;
  const winPoints = <img
    className={'policy-description-icon policy-description-icon-margin-left'}
    src={'/img/icons/win points.png'}
    alt={'Очки'}
    title={'Очки победы'}
  />;
  const city = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-right-left'}
    src={'/img/icons/city.42008716.png'}
    alt={'Город'}
    title={'Город'}
  />;
  const action = <img
    className={'policy-description-icon policy-description-icon-margin-left'}
    src={'/img/icons/action.png'}
    alt={'Действие'}
    title={'Действие в Фазе Строительства'}
  />;
  const supply = <img
    className={'policy-description-icon policy-description-icon-margin-left'}
    src={'/img/icons/supply.png'}
    alt={'Снабжение'}
    title={'Снабжение'}
  />;
  const barracks = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-right-left'}
    src={'/img/hexes/barracks.efab12f7.png'}
    alt={'Казарма'}
    title={'Казарма'}
  />;
  const dice = <img
    className={'policy-description-icon policy-description-icon-margin-left'}
    src={'/img/icons/dice.png'}
    alt={'Кость урона'}
    title={'Кость урона'}
  />;

  const godBonus = getLeagueBonus(LEAGUES.GOD);
  // Фиксированная прибавка к доходу у всех лиг с бонусом одна — берется у серебра
  const fixedIncomeCoins = getLeagueBonus(LEAGUES.SILVER).coinsIncomeBonus;

  return (
    <div className="rules-page_content">
      <h2 className="rules-page_title">
        <img
          className={'rules-page_title-icon'}
          src={getVisibleBotIconPath(LEAGUES.GOLD, true)}
          alt={''}
          aria-hidden={'true'}
        />
        Боты
      </h2>

      <div className="rules-page_section">
        <p>
          В рейтинговом матче соперником может оказаться бот. Такой бот помечен для всех игроков значком рядом с
          именем, а цвет значка показывает лигу бота — от бронзовой до божественной. Подсказка на значке
          расшифровывает бонусы бота.
        </p>
        <p>
          Чем выше лига бота, тем больше у него бонусов: экономических (стартовые монеты, прибавка к доходу монет,
          которая начиная с золотой лиги растет с каждым городом, действия в фазе Строительства), а начиная с
          алмазной — еще и к росту размера отрядов за снабжение.
          Божественный бот сверх того получает очки победы к доходу за каждый город и кость урона, которая всегда
          дает {formatDiceFace(DAMAGE_DICE_MAX_VALUE)} (кроме боя с другими ботами): бонусы к костям урона есть только
          у божественных ботов.
        </p>
      </div>

      <div className="rules-page_section">
        <h2>Экономические бонусы</h2>
        <ul>
          <li>
            <b>Стартовые монеты</b>{coins} — прибавка к монетам, которые бот получает в момент старта матча.
          </li>
          <li>
            <b>Доход монет</b>{coins} — прибавка к доходу в конце каждого раунда: +{fixedIncomeCoins}, пока у бота есть
            хотя бы один город {city}, — у всех лиг с бонусом поровну, а начиная с золотой лиги еще и за каждый город,
            которым владеет бот, даже если кварталы города заблокированы. Чем больше у бота городов, тем больше эта часть
            прибавки, и каждый отбитый у бота город ее уменьшает. Без городов прибавки нет, а если в конце раунда у бота
            не осталось ни одного города, его монеты сгорают — как у любого игрока (см. «Захват города» в разделе
            «Очки за действия»).
          </li>
          <li>
            <b>Действия в фазе Строительства</b>{action} — прибавка к лимиту действий бота в каждой фазе
            Строительства.
          </li>
        </ul>
      </div>

      <div className="rules-page_section">
        <h2>Очки победы к доходу</h2>
        <p>
          Божественный бот в конце каждого раунда получает к доходу
          еще {formatWinPoints(godBonus.winPointsIncomePerCityBonus)}{winPoints} за каждый свой город {city} — так же,
          как бонус к доходу монет. Без городов этих очков он не получает.
        </p>
      </div>

      <div className="rules-page_section">
        <h2>Бонус к росту отрядов</h2>
        <p>
          Обычно снабженный {supply} отряд в фазе Снабжения растет на {UNIT_SIZE_FOR_SUPPLY_ON_BARRACKS}, если стоит
          на казарме {barracks}, и на {UNIT_SIZE_FOR_SUPPLY_ON_OTHER} в остальных местах (см. раздел «Фаза
          Снабжения»). У ботов старших лиг отряды растут быстрее: к этому росту прибавляется 1 — у одних ботов только
          на казарме, у других везде.
        </p>
        <p>
          Прибавка дается один раз за фазу — к росту за первое базовое снабжение отряда. Великаны, которых можно
          снабжать многократно, получают ее только за первое снабжение, остальные снабжения растят отряд как обычно.
          Где отряд за снабжение не растет вовсе (d4 вне казармы, инсектоиды на пустой ячейке), не растет он и с
          прибавкой. Предельный размер отряда не меняется.
        </p>
      </div>

      <div className="rules-page_section">
        <h2>Кость урона божественного бота</h2>
        <p>
          Обычная кость урона {dice} дает от {formatDiceFace(DAMAGE_DICE_MIN_VALUE)} до{' '}
          {formatDiceFace(DAMAGE_DICE_MAX_VALUE)} (см. раздел «Фаза Войны»). У божественного бота каждый бросок d6
          дает {godBonus.d6MinRoll}, поэтому его кость урона всегда {formatDiceFace(DAMAGE_DICE_MAX_VALUE)} — лучшая
          грань.
        </p>
        <p>
          Так у него выпадают все кости урона: в ближнем бою (и когда он атакует, и когда защищается), при стрельбе и
          при перебросе. Если божественный бот играет за гномов, разведка рудника у него всегда находит великий
          рудник.
        </p>
        <p>
          Исключение — бой с другими ботами: против любого бота божественный бот бросает обычные кости урона — и в
          ближнем бою, и при стрельбе. Если его отряд в ближнем бою зажат сразу несколькими противниками, хватает
          одного бота среди них. Выстрел, который задевает строение бота, — тоже бой с ботом, даже если на этой
          ячейке стоит отряд живого игрока. Против нейтральных отрядов кость урона по-прежнему всегда{' '}
          {formatDiceFace(DAMAGE_DICE_MAX_VALUE)}. На разведку рудника исключение не распространяется.
        </p>
      </div>

      <div className="rules-page_section">
        <h2>Бонусы по лигам</h2>
        <BotLeagueBonuses/>
      </div>

      <div className="rules-page_section">
        <h2>Боты в локальной игре</h2>
        <p>
          В локальной игре уровень бонуса каждому боту выбирается при настройке партии. Бот получает все, что дает
          сам уровень, — экономику{WIN_POINTS_LEVELS_TEXT && ` (${WIN_POINTS_LEVELS_TEXT})`}, а сверх того — бонусы
          бота рейтингового матча с тем же значком:
        </p>
        <ul>
          {Object.values(LEAGUES).map((leagueId) => {
            const bonusLabels = getLocalBotBonusTypes(leagueId).map(getBonusLabel);
            return bonusLabels.length > 0 && (
              <li key={leagueId}>
                <b>Бонус {bonusLabels.join(' или ')}</b> — значок {LEAGUE_ADJECTIVES_GENITIVE[leagueId]} бота.
                {' '}{getLocalBotExtrasText(leagueId)}
              </li>
            );
          })}
        </ul>
        <p>
          Бонусы к росту отрядов и к костям урона получают только боты: живому игроку тот же уровень бонуса дает лишь
          экономику{WIN_POINTS_LEVELS_TEXT && `, а ${WIN_POINTS_LEVELS_TEXT}`}.
        </p>
      </div>
    </div>
  );
};

export default BotsRules;
